import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {AbortController|null} Cancels stale in-flight qty/remove requests */
  #currentAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Collects all cart row elements associated with a given row — including
   * Shopify-native nested lines (via data-parent-key) AND custom bundle
   * add-on rows (via data-bundle-id).
   *
   * @param {HTMLTableRowElement} primaryRow - The row being removed.
   * @returns {HTMLTableRowElement[]} All rows that should be removed together.
   */
  #collectRelatedRows(primaryRow) {
    const rows = [primaryRow];
    const key = primaryRow.dataset.key;
    const bundleId = primaryRow.dataset.bundleId;

    for (const row of this.refs.cartItemRows) {
      if (row === primaryRow) continue;

      // Shopify-native nested lines (e.g. bundle components)
      if (key && row.dataset.parentKey === key) {
        rows.push(row);
        continue;
      }

      // Custom bundle add-on rows sharing the same bundle ID
      if (bundleId && row.dataset.bundleId === bundleId) {
        rows.push(row);
        continue;
      }
    }

    return rows;
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const rowsToRemove = this.#collectRelatedRows(cartItemRowToRemove);

    // NOTE: We intentionally do NOT call this.#removeAddonLines here anymore.
    // The atomic update in updateQuantity (above) already removes every
    // bundle-sibling line in a single /cart/update.js call. Firing a second
    // /cart/update.js for the same addon keys created a race condition with
    // the abort controller in updateQuantity — under the wrong timing, the
    // atomic call would get aborted, leaving the master line stuck in the
    // cart. Single source of truth = updateQuantity.

    const visibleRows = this.refs.cartItemRows.filter(
      (row) => !row.classList.contains('cart-addon-hidden')
    );
    const visibleRowsToRemove = rowsToRemove.filter(
      (row) => !row.classList.contains('cart-addon-hidden')
    );
    const isEmptyCart = visibleRowsToRemove.length >= visibleRows.length;

    const template = document.getElementById('empty-cart-template');

    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);
      return;
    }

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Quietly removes bundle add-on line items from the server-side cart.
   *
   * @param {HTMLTableRowElement} primaryRow - The base product row.
   * @param {HTMLTableRowElement[]} allRelatedRows - All rows collected for this removal.
   */
  #removeAddonLines(primaryRow, allRelatedRows) {
    const addonKeys = allRelatedRows
      .filter((row) => row !== primaryRow && row.dataset.key)
      .map((row) => row.dataset.key);

    if (addonKeys.length === 0) return;

    const updates = {};
    for (const key of addonKeys) {
      updates[key] = 0;
    }

    const body = JSON.stringify({ updates });

    fetch('/cart/update.js', fetchConfig('json', { body }))
      .catch((err) => console.error('Failed to remove bundle add-on lines:', err));
  }

  /**
   * Syncs bundle add-on line item quantities to match the base product quantity.
   * Fires after a quantity change so addon lines stay in sync.
   *
   * @param {HTMLTableRowElement} primaryRow - The base product row that changed.
   * @param {number} newQuantity - The new quantity to sync addons to.
   */
  #syncAddonQuantities(primaryRow, newQuantity) {
    const bundleId = primaryRow.dataset.bundleId;
    if (!bundleId) return;

    const addonKeys = [];
    for (const row of this.refs.cartItemRows) {
      if (row === primaryRow) continue;
      if (row.dataset.bundleId === bundleId && row.dataset.key) {
        addonKeys.push(row.dataset.key);
      }
    }

    if (addonKeys.length === 0) return;

    const updates = {};
    for (const key of addonKeys) {
      updates[key] = newQuantity;
    }

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates }),
    })
      .then(() => {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      })
      .catch((err) => console.error('Failed to sync bundle add-on quantities:', err));
  }

  /**
   * Sweeps the cart for orphan addon line items — addons whose _bundle_id
   * has no corresponding master line item — and silently removes them.
   * Called after every cart update so partial-state issues self-heal.
   *
   * @param {Object} cart - Parsed /cart/update.js or /cart/change.js response
   *                       (contains a `cart` object via the standard Shopify
   *                       cart sections response, or the cart object itself).
   */
  #cleanupOrphansFromCart(cart) {
    const items = cart?.items;
    if (!Array.isArray(items) || items.length === 0) return;

    const ADDON_KEYWORDS = ['length-upcharge', 'connector-upcharge', 'label-upcharge', 'sleeve-upcharge'];
    const isAddonHandle = (h) => typeof h === 'string' && ADDON_KEYWORDS.some((k) => h.includes(k));

    // Bundles that have a master (a non-addon line item).
    const masterBundles = new Set();
    for (const item of items) {
      const bid = item?.properties?._bundle_id;
      if (bid && !isAddonHandle(item.handle)) {
        masterBundles.add(bid);
      }
    }

    // Orphans = addons whose bundle has no master.
    const orphanKeys = [];
    for (const item of items) {
      const bid = item?.properties?._bundle_id;
      if (bid && isAddonHandle(item.handle) && !masterBundles.has(bid)) {
        orphanKeys.push(item.key);
      }
    }

    if (orphanKeys.length === 0) return;

    console.log('[Cart] Removing orphan addon lines (no parent master):', orphanKeys);
    const updates = {};
    for (const k of orphanKeys) updates[k] = 0;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        updates,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      }),
    })
      .then((r) => r.text())
      .then((t) => {
        const result = JSON.parse(t);
        if (result?.sections?.[this.sectionId]) {
          morphSection(this.sectionId, result.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
        }
      })
      .catch((err) => console.warn('[Cart] Orphan cleanup failed:', err));
  }

  /**
   * Updates the quantity of a single line item.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    if (this.#currentAbortController) {
      this.#currentAbortController.abort();
    }
    this.#currentAbortController = new AbortController();
    const { signal } = this.#currentAbortController;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Capture the primary row before the fetch so we can read its bundle id
    const primaryRow = this.refs.cartItemRows[line - 1];

    // Detect bundle: if this row has a bundle id, batch the base + all addon line
    // updates into a single /cart/update.js call so the cart re-renders exactly once
    // with the correct total. Otherwise change.js would render with the new base
    // quantity but stale addon quantities, briefly showing a wrong cart total
    // before a second round-trip resyncs the addons.
    const bundleId = primaryRow?.dataset.bundleId;
    const primaryKey = primaryRow?.dataset.key;
    const useBundleAtomicUpdate = Boolean(bundleId && primaryKey);

    let endpoint;
    let body;

    if (useBundleAtomicUpdate) {
      const updates = {};
      updates[primaryKey] = quantity;
      for (const row of this.refs.cartItemRows) {
        if (row === primaryRow) continue;
        if (row.dataset.bundleId === bundleId && row.dataset.key) {
          updates[row.dataset.key] = quantity;
        }
      }
      endpoint = '/cart/update.js';
      body = JSON.stringify({
        updates,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });
    } else {
      endpoint = `${Theme.routes.cart_change_url}`;
      body = JSON.stringify({
        line: line,
        quantity: quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });
    }

    cartTotal?.shimmer();

    const fetchOptions = fetchConfig('json', { body });
    fetchOptions.signal = signal;

    fetch(endpoint, fetchOptions)
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
        this.#updateCartQuantitySelectorButtonStates();

        // For bundle changes, the atomic /cart/update.js call above already synced
        // every line in a single render. For non-bundle changes there's nothing to
        // sync. The legacy #syncAddonQuantities path is kept below as a safety net
        // for the (rare) case where bundle detection misses — e.g. a base row that
        // somehow lost its data-bundle-id while addons still have one.
        if (!useBundleAtomicUpdate && quantity > 0 && primaryRow) {
          this.#syncAddonQuantities(primaryRow, quantity);
        }

        // Defensive cleanup: after every cart update, scan the response for
        // orphan addons (addon line items whose _bundle_id has no master in
        // the cart anymore) and silently remove them. Catches edge cases
        // where the trash button's atomic update raced something, a previous
        // session left orphans behind, or some other partial-state scenario.
        this.#cleanupOrphansFromCart(parsedResponseText);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}